import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Panel from "../central/Panel";
import Button from "../central/Button";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { advanceSeason } from "../utils/advanceSeason";
import { computeSeasonStartPrices } from "../utils/seasonPriceEngine";
import SeasonManualRating from "../components/SeasonManualRating";
import PageLoader from "../components/PageLoader";
import { useToast } from "../context/ToastContext";
import LoadingOverlay from "../central/LoadingOverlay";

const EndOfSeasonReview = () => {
  const { leagueID } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { state } = useLocation();

  const [samplePlayer, setSamplePlayer] = React.useState(null);
  const [loadingSample, setLoadingSample] = React.useState(true);
  const [currentSeasonNum, setCurrentSeasonNum] = React.useState(null);
  const [nextSeasonNum, setNextSeasonNum] = React.useState(null);
  const [showFinalizeModal, setShowFinalizeModal] = React.useState(false);
  const [runningFinalize, setRunningFinalize] = React.useState(false);
  const [autoPreview, setAutoPreview] = React.useState(null);
  const [manualPlayers, setManualPlayers] = React.useState(null);

  React.useEffect(() => {
    if (!currentSeasonNum) return;
    const loadSample = async () => {
      try {
        const colRef = collection(db, "leagues", leagueID, "seasons", `S${currentSeasonNum}`, "players");
        const snap = await getDocs(colRef);
        if (state?.priceChoice === "manual") {
          const players = snap.docs.map(d => ({
            id: d.id,
            name: d.data().name,
            position: d.data().position,
            originalPrice: d.data().originalPrice,
            currentPrice: d.data().currentPrice ?? d.data().originalPrice,
            rating: d.data().rating ?? null
          }));
          setManualPlayers(players);
        }
        if (!snap.empty) {
          const doc = snap.docs[0];
          setSamplePlayer({
            name: doc.data().name,
            originalPrice: doc.data().originalPrice
          });

          if (state?.priceChoice === "auto") {
            try {
              const results = await computeSeasonStartPrices({ leagueID });

              if (results && results.pricesByPlayer) {
                const entries = Object.entries(results.pricesByPlayer);

                if (entries.length > 0) {
                  // Pick player with largest positive season drift
                  entries.sort(([, a], [, b]) => {
                    return (b.debug?.drift ?? 0) - (a.debug?.drift ?? 0);
                  });

                  const [playerID, priceInfo] = entries[0];

                  // Look up player name from previously loaded sample players
                  const playerSnap = snap.docs.find(d => d.id === playerID);

                  setAutoPreview({
                    name: playerSnap?.data()?.name || "Example Player",
                    oldPrice: priceInfo.baseOriginal.toFixed(1),
                    newPrice: priceInfo.newOriginal.toFixed(1)
                  });
                }
              }
            } catch (err) {
              console.error("Failed to generate auto price preview", err);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load sample player", err);
      } finally {
        setLoadingSample(false);
      }
    };
    loadSample();
  }, [leagueID, currentSeasonNum]);

  React.useEffect(() => {
    const loadSeasonNumbers = async () => {
      try {
        const leagueRef = doc(db, "leagues", leagueID);
        const leagueSnap = await getDoc(leagueRef);
        if (leagueSnap.exists()) {
          const data = leagueSnap.data();
          const seasonStr = data.currentSeasonID || "S1";
          const curNum = parseInt(seasonStr.replace("S",""),10) || 1;
          setCurrentSeasonNum(curNum);
          setNextSeasonNum(curNum + 1);
        }
      } catch (err) {
        console.error("Failed loading season numbers", err);
      }
    };
    loadSeasonNumbers();
  }, [leagueID]);

  // If someone navigates here without going through page 1
  if (!state) {
    return (
      <>
        <Panel>
          <h2>Error</h2>
          <p>No season settings found. Please restart the End of Season process.</p>
          <Button onClick={() => navigate(`/endofseason/${leagueID}`)}>
            Back to Start
          </Button>
        </Panel>
      </>
    );
  }

  const {
    priceChoice,
    matchDay,
    timeOfDay,
    chipsEnabled,
    computedDeadline
  } = state;

  return (
    <>
      <LoadingOverlay show={runningFinalize} message="Finalizing season…" />
      <Panel style={{ maxWidth: "650px", margin: "2rem auto" }}>
        <h2 style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          Review Season Settings
        </h2>

        <div style={{ marginBottom: "2rem" }}>
          <p><b>Initial Deadline:</b> {computedDeadline?.formatted}</p>
          <p><b>Match Day:</b> {matchDay?.value}</p>
          <p><b>Match Time:</b> {timeOfDay?.value}</p>
          <p><b>Chips Enabled:</b> {chipsEnabled ? "Yes" : "No"}</p>
          <p style={{ marginTop: "1.0rem" }}><b>Price Rule:</b> {priceChoice}</p>
          {priceChoice === "manual" ? (
            <SeasonManualRating
              leagueID={leagueID}
              players={manualPlayers || []}
              setPlayers={setManualPlayers}
            />
          ) : loadingSample ? (
            <PageLoader message="Loading example…" />
          ) : samplePlayer ? (
            priceChoice === "auto" && autoPreview ? (
              <p style={{ marginTop: "0.2rem", fontStyle: "italic" }}>
                Example: {autoPreview.name} | Season {currentSeasonNum} Price = ${autoPreview.oldPrice}m → Season {nextSeasonNum} Price = ${autoPreview.newPrice}m
              </p>
            ) : (
              <p style={{ marginTop: "0.2rem", fontStyle: "italic" }}>
                Example: {samplePlayer.name} | Season {currentSeasonNum} Price = ${samplePlayer.originalPrice}m → Season {nextSeasonNum} Price = ${samplePlayer.originalPrice}m
              </p>
            )
          ) : (
            <p><i>No players found for example display.</i></p>
          )}
        </div>

        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2rem"
        }}>
          <Button
            variant="blue"
            onClick={() => navigate(`/endofseason/${leagueID}`)}
          >
            Back
          </Button>

          <Button
            variant="pink"
            onClick={async () => {
              setRunningFinalize(true);
              try {
                const result = await advanceSeason({
                  leagueID,
                  priceRule: priceChoice,
                  matchDay,
                  timeOfDay,
                  chipsEnabled,
                  computedDeadline,
                  manualPlayers: priceChoice === "manual" ? (manualPlayers || []) : manualPlayers
                });

                if (result && result.success) {
                  navigate(`/admin/${leagueID}`);
                  setRunningFinalize(false);
                } else {
                  console.error("Advance season failed:", result?.error || "No result returned");
                  showToast("Failed to finalize season. Please try again.", "error");
                  setRunningFinalize(false);
                }
              } catch (err) {
                console.error("Unexpected error finalizing season:", err);
                showToast("An unexpected error occurred. Please try again.", "error");
                setRunningFinalize(false);
              }
            }}
          >
            Confirm Finalize
          </Button>
        </div>
      </Panel>

      {showFinalizeModal && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <Panel style={{ maxWidth: "450px", padding: "2rem" }}>
            <p style={{ marginBottom: "1.5rem" }}>
              Are you sure you want to finalize Season {currentSeasonNum} and create Season {nextSeasonNum}? This cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="blue" onClick={() => setShowFinalizeModal(false)}>
                Back
              </Button>
              <Button
                variant="pink"
                onClick={async () => {
                  setRunningFinalize(true);
                  try {
                    const result = await advanceSeason({
                      leagueID,
                      priceRule: priceChoice,
                      matchDay,
                      timeOfDay,
                      chipsEnabled,
                      computedDeadline,
                      manualPlayers: priceChoice === "manual" ? manualPlayers : null
                    });

                    if (result && result.success) {
                      navigate(`/admin/${leagueID}`);
                      setRunningFinalize(false);
                    } else {
                      console.error("Advance season failed:", result?.error || "No result returned");
                      showToast("Failed to finalize season. Please try again.", "error");
                      setRunningFinalize(false);
                    }
                  } catch (err) {
                    console.error("Unexpected error finalizing season:", err);
                    showToast("An unexpected error occurred. Please try again.", "error");
                    setRunningFinalize(false);
                  }
                }}
              >
                Confirm Finalize
              </Button>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
};

export default EndOfSeasonReview;